import type { PlacementFormState, WorkerOrgScenario } from "@/components/ats-fbo/types"

/** The fields the source prefills. Any other field is the reviewer's own and is never replaced. */
const SOURCE_PREFILLED_FIELDS = [
  "internalCompanyId",
  "clientOrgId",
  "workerEmail",
  "profileTypeCode",
  "workerPreferredCultureCode",
  "startDate",
  "endDate",
  "billRate",
  "payRate",
  "billCurrencyCode",
  "payCurrencyCode",
  "lobCode",
] as const satisfies readonly (keyof PlacementFormState)[]

function copyField<K extends keyof PlacementFormState>(
  target: PlacementFormState,
  source: PlacementFormState,
  key: K,
): void {
  target[key] = source[key]
}

function isSameWorkerOrg(a: WorkerOrgScenario | null, b: WorkerOrgScenario | null): boolean {
  if (a == null || b == null) return a == b
  if (a.kind === "existing" && b.kind === "existing") return a.organizationId === b.organizationId
  if (a.kind === "new" && b.kind === "new") return a.organizationName === b.organizationName
  return a.kind === b.kind
}

/**
 * Brings the form up to date with a source that was read again, keeping every field the reviewer changed.
 *
 * @param current the form as the reviewer has it now.
 * @param before the form as it was built from the previous source.
 * @param after the form as it would be built from the new source.
 * @param options.keepEmail keeps the reviewer's email even when unchanged, while they are editing it.
 */
export function mergeRefreshedForm(
  current: PlacementFormState,
  before: PlacementFormState,
  after: PlacementFormState,
  options: { keepEmail: boolean },
): PlacementFormState {
  const merged: PlacementFormState = { ...current }
  for (const key of SOURCE_PREFILLED_FIELDS) {
    if (current[key] === before[key]) copyField(merged, after, key)
  }
  if (isSameWorkerOrg(current.workerOrgScenario, before.workerOrgScenario)) {
    merged.workerOrgScenario = after.workerOrgScenario
  }
  if (options.keepEmail) merged.workerEmail = current.workerEmail

  // A different email is a different worker, as when the reviewer changes it.
  if (merged.workerEmail !== current.workerEmail) {
    merged.workerScenario = null
    merged.workerOrgScenario = null
  }
  return merged
}
